import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  IconButton,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { projectService, personaService } from '../services/api';
import type { Project, Persona } from '@personawiz/shared';

export default function ProjectDetail() {
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  const id = pathId || queryId;
  const navigate = useNavigate();
  
  const [project, setProject] = useState<Project | null>(null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Add persona dialog state
  const [addPersonaOpen, setAddPersonaOpen] = useState(false);
  const [availablePersonas, setAvailablePersonas] = useState<Persona[]>([]);
  const [personaSearch, setPersonaSearch] = useState('');
  const [isLoadingPersonas, setIsLoadingPersonas] = useState(false);
  const [isAddingPersona, setIsAddingPersona] = useState(false);

  const loadProject = useCallback(async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const [projectData, personasData] = await Promise.all([
        projectService.getById(id!),
        projectService.listPersonas(id!),
      ]);
      
      setProject(projectData);
      setPersonas(personasData.data || []);
    } catch (err) {
      console.error('Failed to load project:', err);
      setError('Failed to load project. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      const timer = setTimeout(() => {
        void loadProject();
      }, 0);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [id, loadProject]);

  async function handleOpenAddPersona() {
    setAddPersonaOpen(true);
    setPersonaSearch('');
    setIsLoadingPersonas(true);
    try {
      const result = await personaService.list({ pageSize: 100 });
      const projectPersonaIds = new Set(personas.map(p => p.id));
      setAvailablePersonas((result.data || []).filter(p => !projectPersonaIds.has(p.id)));
    } catch (err) {
      console.error('Failed to load personas:', err);
    } finally {
      setIsLoadingPersonas(false);
    }
  }

  async function handleAddPersonaToProject(personaId: string) {
    if (!project) return;
    setIsAddingPersona(true);
    try {
      await projectService.addPersona(project.id, personaId);
      const added = availablePersonas.find(p => p.id === personaId);
      if (added) {
        setPersonas(prev => [...prev, added]);
        setAvailablePersonas(prev => prev.filter(p => p.id !== personaId));
      }
    } catch (err) {
      console.error('Failed to add persona:', err);
    } finally {
      setIsAddingPersona(false);
    }
  }

  const filteredAvailablePersonas = availablePersonas.filter(p => {
    if (!personaSearch.trim()) return true;
    const q = personaSearch.toLowerCase();
    return (
      p.coreIdentity.personaName.toLowerCase().includes(q) ||
      p.coreIdentity.userRole.toLowerCase().includes(q) ||
      p.coreIdentity.useCase.toLowerCase().includes(q)
    );
  });

  function handleEditClick() {
    setMenuAnchor(null);
    if (project) {
      setEditName(project.name);
      setEditDescription(project.description || '');
      setEditDialogOpen(true);
    }
  }

  async function handleSaveEdit() {
    if (!project || !editName.trim()) return;
    
    setIsSaving(true);
    try {
      const input: { name?: string; description?: string } = {};
      if (editName.trim() !== project.name) {
        input.name = editName.trim();
      }
      if (editDescription.trim() !== (project.description || '')) {
        if (editDescription.trim()) {
          input.description = editDescription.trim();
        }
      }
      
      if (Object.keys(input).length > 0) {
        const updated = await projectService.update(project.id, input);
        setProject(updated);
      }
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Failed to update project:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function handleDeleteClick() {
    setMenuAnchor(null);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!project) return;
    
    setIsDeleting(true);
    try {
      await projectService.delete(project.id);
      navigate('/');
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  }

  async function handleRemovePersona(personaId: string) {
    if (!project) return;
    
    try {
      await projectService.removePersona(project.id, personaId);
      setPersonas(personas.filter(p => p.id !== personaId));
    } catch (err) {
      console.error('Failed to remove persona from project:', err);
    }
  }

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="text" width={300} />
        <Grid container spacing={2} sx={{ mt: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error && !project) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box>
        <Alert severity="warning">Project not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          underline="hover"
          color="inherit"
        >
          Dashboard
        </Link>
        <Typography color="text.primary">{project.name}</Typography>
      </Breadcrumbs>

      {/* Project Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {project.name}
          </Typography>
          {project.description && (
            <Typography variant="body1" color="text.secondary">
              {project.description}
            </Typography>
          )}
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Chip
              size="small"
              icon={<PersonIcon />}
              label={`${personas.length} persona${personas.length !== 1 ? 's' : ''}`}
            />
            <Chip
              size="small"
              label={project.status}
              color={project.status === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        </Box>
        
        <Box>
          
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={handleEditClick}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Edit Project
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Delete Project
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Personas List */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Personas in this Project
        </Typography>
        {personas.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAddPersona}
            >
              Add Persona
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/personas/new', { state: { projectId: project.id, projectName: project.name } })}
            >
              Generate Persona
            </Button>
          </Box>
        )}
      </Box>

      {personas.length > 0 ? (
        <Grid container spacing={2}>
          {personas.map((persona) => (
            <Grid item xs={12} md={4} key={persona.id}>
              <Card>
                <CardActionArea onClick={() => navigate(`/personas/${persona.id}`)}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {persona.coreIdentity.personaName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {persona.coreIdentity.userRole} • {persona.coreIdentity.useCase}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {persona.coreIdentity.northStarMetric}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePersona(persona.id);
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No personas in this project
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate a persona and add it to this project.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddPersona}
            >
              Add Existing Persona
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/personas/new', { state: { projectId: project.id, projectName: project.name } })}
              sx={{ ml: 1 }}
            >
              Generate New Persona
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Persona Dialog */}
      <Dialog open={addPersonaOpen} onClose={() => setAddPersonaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Existing Persona</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search personas..."
            value={personaSearch}
            onChange={(e) => setPersonaSearch(e.target.value)}
            size="small"
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.disabled', mr: 1 }} />,
            }}
          />
          {isLoadingPersonas ? (
            <Box>
              {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />)}
            </Box>
          ) : filteredAvailablePersonas.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {availablePersonas.length === 0 ? 'No personas available to add.' : 'No personas match your search.'}
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredAvailablePersonas.map(persona => (
                <Card key={persona.id} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {persona.coreIdentity.personaName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {persona.coreIdentity.userRole} &bull; {persona.coreIdentity.useCase}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleAddPersonaToProject(persona.id)}
                      disabled={isAddingPersona}
                      sx={{ ml: 2, flexShrink: 0 }}
                    >
                      Add
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPersonaOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!editName.trim() || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{project.name}"? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Note: Personas in this project will not be deleted, but they will be unassociated from this project.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
